export interface OAuthResponse {
  email: string;
  accountId: string;
  accessToken: string;
  refreshToken: string;
  total_accounts_count: number;
  accounts: GoogleAdsAccount[];
  google_social_account_id?: number;
  ad_accounts?: GoogleAdsAccount[];
  user_info?: {
    email: string;
    name: string;
  };
  success?: boolean;
  message?: string;
  [key: string]: any;
}

export interface OAuthDetails {
  authorization_url: string;
}

export enum ADDON_ACCOUNT_STATUS {
  UNSPECIFIED = "UNSPECIFIED",
  UNKNOWN = "UNKNOWN",
  ENABLED = "ENABLED",
  CANCELED = "CANCELED",
  SUSPENDED = "SUSPENDED",
  CLOSED = "CLOSED",
}

export interface GoogleAdsAccount {
  id: string;
  name: string;
  currencyCode?: string;
  currency_code?: string; // API alias
  publisherId?: string;
  reportingTimeZone?: string;
  time_zone?: string; // API alias
  status?: ADDON_ACCOUNT_STATUS | string;
  is_manager?: boolean;
  social_account_id?: number;
  google_social_account_id?: number;
}

// New interface for addon accounts
export interface IAddonAccount {
  id: string;
  email: string;
  account_name: string;
  account_id: string;
  platform: "google_ads" | "google_admob";
  is_active: boolean;
  accounts?: GoogleAdsAccount[];
  selected_account_id?: string;
}

export interface IIntegrationList {
  [key: string]: IIntegrations;
}

export interface IIntegrations {
  inwards: IIntegrationsSet[];
  outwards: IIntegrationsSet[];
  name?: string;
  own_integration: boolean;
  inwards_count: number;
  outwards_count: number;
  addon_accounts?: IAddonAccount[];
  is_ongoing?: boolean;
  creator_name?: string;
  creator_email?: string;
}

export interface IIntegrationsSet {
  id?: number;
  type: string;
  email?: string;
  admob_publisher_id?: string | null;
  ads_parent_id?: number;
  ads_sub_account_id?: string | null;
  currency?: string | null;
  is_active?: boolean;
  accounts?: GoogleAdsAccount[];
  selected_account_id?: string;
  user_name?: string;
  user_id?: string;
  timezone_str?: string;
  apple_iap_vendor_number?: string;
  ads_parent_account_name?: string;
  created_at?: string;
  updated_at?: string;
  is_deleted?: boolean;
  ads_sub_account_name?: string | null;
  apple_iap_key_id?: string | null;
  apple_iap_issuer_id?: string | null;
  apple_ads_key_id?: string | null;
  apple_ads_team_id?: string | null;
  apple_ads_client_id?: string | null;
  google_iap_service_account_email?: string | null;
  google_iap_bucket_name?: string | null;
  category_type?: "inward" | "outward";
  status?: "pending" | "in_progress" | "finished" | "failed" | "not_found" | null;
  social_account_id?: number;
  google_social_account_id?: number;
}

export type TDirection = "inward" | "outward" | null;

export interface IIntegrationsComponentProps {
  integrations?: IIntegrations[];
  onUpdateIntegration: (
    index: number,
    direction: TDirection,
    newEmail: string
  ) => void;
  onToggleConnection: (index: number, direction: TDirection) => void;
  onAddNewIntegrations: (payload: IIntegrations) => void;
  onDeleteIntegration: (index: number) => void;
}

export interface IInitialModalStateInterface {
  index: number;
  open: boolean;
  direction: TDirection;
  email: string;
}

export interface IInitialConfirmationPopUpInterface {
  index: number;
  open: boolean;
  direction: "inward" | "outward" | null;
  type: "connect" | "disconnect" | "updateEmail" | null;
}

// New interface for addon account modal
export interface IAddonAccountModalState {
  open: boolean;
  trackingId: string;
  platform: "google_ads" | "google_admob";
  email: string;
  accounts: GoogleAdsAccount[];
  selectedAccountId: string;
}

export interface ICheckOnGoingIntegrationResponse {
  tracking_id: string;
  name: string;
  inwards: IIntegrationsSet[] | null;
  outwards: IIntegrationsSet[] | null;
  user_id: string;
  is_ongoing: boolean;
  created_at?: string;
}

export interface IIntegrationResponse {
  [key: string]: {
    inwards: IIntegrationsSet[];
    outwards: IIntegrationsSet[];
    own_integration: boolean;
    is_ongoing: boolean;
    inwards_count: number;
    outwards_count: number;
    name: string;
    creator_name?: string;
    creator_email?: string;
  };
}

export interface IAccountsEntity {
  id: string;
  name: string;
  currency_code: string;
  publisher_id?: string;
  reporting_time_zone?: string;
  status?: ADDON_ACCOUNT_STATUS;
  social_account_id?: number;
  google_social_account_id?: number;
}

export interface IAclOrganization {
  orgName: string;
  orgId: number;
  currency: string;
  timeZone: string;
  paymentModel: string;
  roleNames: string[];
  parentOrgId: number | null;
  displayName: string;
}

export interface IAppleSearchAdsResponse {
  message: string;
  tracking_id: string;
  token_expires_at: string;
  acl_response: {
    data: IAclOrganization[];
    pagination?: null;
    error: null | string;
  };
  social_account_id: number;
}

export interface IInsertAppsResponse {
  message: string;
  tracking_id: string;
  task_id: string;
  is_addition: boolean;
}

export type SourceType = 'spending' | 'revenue';

export interface AppleIAPFormData {
  p8File: File | null;
  keyId: string;
  issuerId: string;
  vendorNumber: string;
}

export interface AppleAdsFormData {
  client_id: string;
  team_id: string;
  key_id: string;
}

export interface GoogleIAPFormData {
  service_account_json: FileList | null;
  email: string;
  bucket_name: string;
}

export interface SelectedAccount {
  id: string;
  name: string;
  email?: string;
  currencyCode?: string;
  status?: string;
  platform?: string;
  platformIcon?: string;
  timezone?: string;
  keyId?: string;
  issuerId?: string;
  serviceAccount?: string;
}

export interface PlatformConfig {
  id: string;
  name: string;
  icon: string;
  type: "spending" | "revenue";
  authType: "oauth" | "form";
  description?: string;
  comingSoon?: boolean;
}
export interface ILatestSocialAccountStatus {
  data: Record<string, string>;
  latest_social_account_id: number;
  latest_finished_sync_time: string;
  latest_social_account_sync_time?: string;
}

export interface IDisconnectAccountRequest {
  trackingId: string;
}

export interface IGoogleOAuthCallbackRequest {
  code: string;
  authorise_type: "google_ads" | "google_admob";
  scope?: string[];
  tracking_id?: string;
}

export interface IGoogleAdsSaveAccountRequest {
  merchant_customer_id: string[];
}

export interface DuplicateAppSocialAccount {
  id: number;
  email: string;
  admob_publisher_id: string;
}

export interface DuplicateApp {
  id: number;
  google_admob_app_id: string;
  google_admob_app_name: string;
  google_admob_app_platform: "ANDROID" | "IOS";
  google_admob_app_logo: string;
  google_admob_store_id: string;
  slug: string;
  is_unlinked: boolean;
  social_account: DuplicateAppSocialAccount;
  last_30_day_revenue: number;
}

export interface DuplicateAppGroup {
  google_admob_store_id: string;
  apps: DuplicateApp[];
}

export interface LinkAppsResponse {
  linked_count: number;
}
